/**
 * lx-ai-modal —— AI 反馈模态框
 *
 * 遮罩用 backdrop-filter: blur（design.md 明确要求）。
 *
 * 模态框是「AI 说话」的唯一出口，因此要同时承载两种内容形态
 * （设计文档 3.6，因为 explain 兼作通用对话出口 D8）：
 *   短句（< 30 字）  → 大字居中，命令反馈的视觉重量感
 *   长文本（≥ 30 字） → 左对齐正文 + 60vh 内部滚动，适合聊天/问答
 *
 * 文案来源遵循信任原则（2.1.1）：
 *   成功 → 解释器本地拼 successText
 *   失败 → 真实错误回传 AI 二次生成
 *   永不出现「AI 预报的成功」
 */

import { LxElement, define } from '@/core/base-element';
import { uiStore } from '@/core/ui-store';
import type { EvalResult } from '@/types/il';

/** 超过这个长度就按长文本排版 */
const LONG_TEXT_THRESHOLD = 30;

export class LxAiModal extends LxElement {
  protected styles(): string {
    return `
      :host {
        position: fixed;
        inset: 0;
        z-index: var(--z-modal);
        display: none;
        align-items: center;
        justify-content: center;
        padding: var(--sp-5);
      }

      :host([open]) {
        display: flex;
      }

      /* 遮罩：需求明确要求背景模糊 */
      .backdrop {
        position: absolute;
        inset: 0;
        background: var(--c-overlay);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        animation: fade var(--dur-base) var(--ease-standard);
      }

      @keyframes fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .card {
        position: relative;
        width: 100%;
        max-width: 340px;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        padding: var(--sp-5);
        background: var(--c-modal-bg);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        animation: pop var(--dur-base) var(--ease-standard);
      }

      @keyframes pop {
        from { opacity: 0; transform: scale(0.96); }
        to { opacity: 1; transform: scale(1); }
      }

      .title {
        font-size: var(--fs-sm);
        font-weight: var(--fw-medium);
        color: var(--c-fg-muted);
        margin-bottom: var(--sp-2);
      }

      /* ---------- 文本区：短句与长文本两套排版 ---------- */
      .text {
        color: var(--c-fg);
        overflow-y: auto;
        max-height: 60vh;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .text.short {
        font-size: var(--fs-xl);
        font-weight: var(--fw-medium);
        line-height: var(--lh-tight);
        text-align: center;
        padding: var(--sp-3) 0;
      }

      .text.long {
        font-size: var(--fs-md);
        line-height: var(--lh-loose);
        text-align: left;
      }

      :host([level='error']) .text {
        color: var(--c-danger);
      }

      /* ---------- 执行结果清单 ---------- */
      .results {
        margin-top: var(--sp-4);
        padding-top: var(--sp-3);
        border-top: 1px solid var(--c-border);
        display: flex;
        flex-direction: column;
        gap: var(--sp-2);
        overflow-y: auto;
        max-height: 30vh;
      }

      .result {
        display: flex;
        gap: var(--sp-2);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
        align-items: flex-start;
      }

      .result .mark {
        flex-shrink: 0;
        width: 16px;
        font-family: var(--font-mono);
      }

      .result.ok {
        color: var(--c-fg);
      }

      .result.ok .mark {
        color: var(--c-success);
      }

      .result.fail {
        color: var(--c-danger);
      }

      .result.fail .mark {
        color: var(--c-danger);
      }

      /* ---------- 操作区 ---------- */
      .actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--sp-2);
        margin-top: var(--sp-5);
      }

      .actions button {
        padding: var(--sp-2) var(--sp-5);
        border-radius: var(--radius-full);
        font-size: var(--fs-sm);
        font-weight: var(--fw-medium);
        transition: opacity var(--dur-fast) var(--ease-standard);
      }

      .actions button:active {
        opacity: 0.7;
      }

      .primary {
        background: var(--c-primary);
        color: var(--c-primary-fg);
      }

      .ghost {
        background: var(--c-surface-2);
        color: var(--c-fg);
      }
    `;
  }

  protected render(): string {
    const { modal } = uiStore.get();
    if (!modal.open) return '';

    const isLong = modal.text.length >= LONG_TEXT_THRESHOLD || modal.text.includes('\n');
    const results = modal.results ?? [];

    return `
      <div class="backdrop" data-close></div>
      <div class="card" role="dialog" aria-modal="true" aria-label="灵讯的回复">
        ${modal.title ? `<p class="title">${this.esc(modal.title)}</p>` : ''}
        <div class="text ${isLong ? 'long' : 'short'}">${this.esc(modal.text)}</div>
        ${results.length ? this.renderResults(results) : ''}
        <div class="actions">
          ${
            modal.level === 'confirm'
              ? `<button class="ghost" type="button" data-cancel>取消</button>
                 <button class="primary" type="button" data-confirm>同意</button>`
              : `<button class="primary" type="button" data-close>知道了</button>`
          }
        </div>
      </div>
    `;
  }

  private renderResults(results: EvalResult[]): string {
    return `
      <div class="results">
        ${results
          .map(
            (r) => `
          <div class="result ${r.ok ? 'ok' : 'fail'}">
            <span class="mark" aria-hidden="true">${r.ok ? '✓' : '✗'}</span>
            <span>${this.esc(r.ok ? r.desc : (r.error?.message ?? '执行失败'))}</span>
          </div>
        `,
          )
          .join('')}
      </div>
    `;
  }

  protected onMounted(): void {
    // 遮罩点击：对 confirm 级来说等同「取消」，所以要触发取消回调
    this.$$('[data-close]').forEach((el) =>
      el.addEventListener('click', () => this.close('cancel')),
    );
    this.$('[data-cancel]')?.addEventListener('click', () => this.close('cancel'));
    this.$('[data-confirm]')?.addEventListener('click', () => {
      const { modal } = uiStore.get();
      const onConfirm = modal.onConfirm;
      // 先关框再执行 —— 让用户立刻看到界面响应，不必等长操作跑完。
      // 回调本身要留到 close 之后再调，因为 close 会清掉 uiStore 里的 modal。
      this.close('confirm');
      onConfirm?.();
    });

    // Esc 关闭：语义上是取消
    this._onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close('cancel');
        return;
      }
      // 焦点陷阱：模态框打开时 Tab 不应该跑到背后的页面上去。
      // 卡片里只有两三个按钮，直接把 Tab 圈在卡片内即可。
      if (e.key === 'Tab') this._trapFocus(e);
    };
    document.addEventListener('keydown', this._onKeydown);

    // 焦点移入，便于键盘用户
    this.$('.card')?.setAttribute('tabindex', '-1');
    (this.$('.card') as HTMLElement | null)?.focus();
  }

  /**
   * 把 Tab / Shift+Tab 的焦点循环限制在卡片内部。
   *
   * 实现思路是「在首尾两端做环绕」：如果当前在最后一个可聚焦元素上按 Tab，
   * 就手动跳回第一个；反之亦然。因为卡片里最多三个按钮，不需要通用
   * 的 tabbable 遍历库。
   */
  private _trapFocus(e: KeyboardEvent): void {
    const card = this.$('.card') as HTMLElement | null;
    if (!card) return;

    const focusables = Array.from(
      card.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute('disabled'));

    if (focusables.length === 0) {
      e.preventDefault();
      card.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = this.shadowRoot?.activeElement ?? null;

    if (e.shiftKey && (active === first || active === card)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private _onKeydown: ((e: KeyboardEvent) => void) | null = null;

  /**
   * 关闭模态框。
   *
   * @param reason 'confirm' 表示用户点了同意（onConfirm 由调用方自己触发）；
   *               其它情况（取消 / Esc / 点遮罩）都会触发 onCancel。
   *
   * 为什么把 onCancel 的触发收拢到这里：
   *   关闭的入口有四个（按钮、Esc、遮罩、外部 close），
   *   如果每个入口各自记得调取消回调，迟早会漏。
   *   统一由 close 决定，就不会漏。
   */
  private close(reason: 'confirm' | 'cancel' | 'dismiss' = 'dismiss'): void {
    const { modal } = uiStore.get();
    const onCancel = modal.onCancel;

    this.removeAttribute('open');
    uiStore.set({ modal: { open: false, level: 'info', text: '' } });
    this.emit('ui:modal-close', {});

    // 只有非确认关闭、且确实是一次确认请求时，才通知「用户拒绝了」
    if (reason !== 'confirm' && modal.level === 'confirm') {
      onCancel?.();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    const sync = (): void => {
      const { modal } = uiStore.get();
      if (modal.open) {
        this.setAttribute('open', '');
        this.setAttribute('level', modal.level);
      } else {
        this.removeAttribute('open');
      }
      this.update();
    };

    this.subscribe('ui:modal-open', (payload) => {
      uiStore.set({
        modal: {
          open: true,
          level: payload.level,
          title: payload.title,
          text: payload.text,
          results: payload.results,
          onConfirm: payload.onConfirm,
        },
      });
      // 必须主动同步一次 —— uiStore.set 不会触发事件总线，
      // 只在下面订阅 modal-close 的话，模态框打开时不会渲染出来
      sync();
    });
    this.subscribe('ui:modal-close', sync);

    // 初始同步（可能已有状态）
    sync();
  }

  protected onUnmounted(): void {
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
  }
}

define('lx-ai-modal', LxAiModal);
